import type { ExperimentWithVerdict } from '@@/api/experiments/implementation-verdict';
import { formatDateTime } from 'helpers/formatters';
import { formatLift } from './result-summary';

export function exportImplementationCsv(experiments: ExperimentWithVerdict[]) {
  const headers = [
    'Experiment Name',
    'Status',
    'Implemented',
    'Implemented Date',
    'Start Date',
    'End Date',
    'Hypothesis',
    'Variant Count',
    'Variant Names',
    // The result, not just the fact that the experiment finished. A developer reading
    // this file needs to know which arm to build and on what evidence.
    'Winning Variant',
    'Winning Goal',
    'Lift',
    'p-value',
  ];

  const rows = experiments.map((exp) => {
    const variantNames = exp.variants?.map((v) => v.name).join('; ') || '';
    const winner = exp.verdict?.winner ?? null;
    return [
      escape(exp.name),
      exp.status,
      exp.implementedAt ? 'Yes' : 'No',
      exp.implementedAt ? formatDateTime(exp.implementedAt) : '',
      formatDateTime(exp.startsAt as number) || '',
      formatDateTime(exp.endsAt as number) || '',
      escape(exp.hypothesis || ''),
      String(exp.variants?.length || 0),
      escape(variantNames),
      escape(winner?.variantName ?? ''),
      winner?.goal ?? '',
      // Runs through escape: a lift starts with + or -, which Excel reads as a formula.
      escape(formatLift(winner?.improvement ?? null)),
      winner?.pValue === null || winner?.pValue === undefined ? '' : String(winner.pValue),
    ].join(',');
  });

  const csv = '\uFEFF' + [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'implementation-experiments.csv';
  // Firefox requires the anchor to be in the document for a programmatic click,
  // and revoking the URL synchronously can cancel the download in flight.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Quote a CSV field and neutralise spreadsheet formulas.
 *
 * Experiment names and hypotheses are user-supplied and this file is meant to be
 * shared with clients, so a value starting with = + - @ (or a leading tab /
 * carriage return, which Excel strips before parsing) would otherwise be
 * evaluated as a formula on open.
 */
function escape(value: string) {
  const neutralised = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;

  if (/[",\n\r]/.test(neutralised)) {
    return `"${neutralised.replace(/"/g, '""')}"`;
  }
  return neutralised;
}
